import { CircularProgress, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { orange, state } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import {
  CERTIFICATE_WARNING_MESSAGE_FORMAL_REQUIRED,
  CERTIFICATE_WARNING_MESSAGE_NOTIFICATION,
} from "@/features/talent/constants/certificate-warning";

type TCertificateFileWarningProps = {
  loading: boolean;
  msg1: boolean;
  msg2: boolean;
};

/**
 * Per-file warning shown below a certificate filename. Two trigger paths:
 * - msg1: file uploaded under 「特定技能の合格書」 but OCR detects 通知書.
 * - msg2: target industry (建設/ビルクリーニング/宿泊) + non-合格書 type chosen,
 *         OR shown alongside msg1 when both apply.
 *
 * Render states:
 * - loading: small orange spinner "確認中..." while the OCR call is in flight.
 * - warning: orange banner with msg1 / msg2 / both lines depending on flags.
 * - idle: null (no DOM).
 */
export const CertificateFileWarning: FC<TCertificateFileWarningProps> = ({ loading, msg1, msg2 }) => {
  if (loading) {
    return (
      <Stack direction="row" alignItems="center" gap={1} mt={1}>
        <CircularProgress size={14} sx={{ color: orange[80] }} />
        <Typography variant="cap12" color={orange[80]}>
          確認中...
        </Typography>
      </Stack>
    );
  }

  if (!msg1 && !msg2) return null;

  return (
    <Stack
      mt={1}
      sx={{
        p: "8px 16px",
        background: state.incomplete_secondary,
        color: orange[80],
      }}
      borderRadius={rounded.xs}
      gap={0.5}
    >
      {msg1 && (
        <Typography variant="cap12" color={orange[80]}>
          {CERTIFICATE_WARNING_MESSAGE_NOTIFICATION}
        </Typography>
      )}
      {msg2 && (
        <Typography variant="cap12" color={orange[80]}>
          {CERTIFICATE_WARNING_MESSAGE_FORMAL_REQUIRED}
        </Typography>
      )}
    </Stack>
  );
};
